import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogOut, LayoutDashboard, Layers, Activity,
  TrendingDown, Settings as SettingsIcon, Sun, Moon,
  User, Lock, Palette, Trash2, CheckCircle2, AlertTriangle, Eye, EyeOff
} from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../hooks/useTheme';
import { CloudMark, Wordmark } from '../components/CloudFrugalLogo';

interface UserProfile {
  id: number;
  email: string;
  created_at: string;
}

export default function Settings() {
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();

  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<'profile' | 'security' | 'appearance' | 'danger'>('profile');

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [savingPassword, setSavingPassword] = useState(false);

  // Delete account state
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteMsg, setDeleteMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => setUser(r.data))
      .catch(() => { localStorage.removeItem('token'); navigate('/login'); })
      .finally(() => setLoading(false));
  }, [navigate, token]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMsg(null);
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    if (newPassword.length < 8) {
      setPasswordMsg({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }
    setSavingPassword(true);
    try {
      await axios.put(`${import.meta.env.VITE_API_BASE_URL}/api/auth/password`,
        { currentPassword, newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPasswordMsg({ type: 'success', text: 'Password updated successfully' });
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err: any) {
      setPasswordMsg({ type: 'error', text: err.response?.data?.message || 'Failed to update password' });
    } finally {
      setSavingPassword(false);
    }
  };

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setDeleteMsg(null);
    if (deleteConfirm !== 'delete my account') {
      setDeleteMsg({ type: 'error', text: 'Please type "delete my account" to confirm' });
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${import.meta.env.VITE_API_BASE_URL}/api/auth/account`,
        { data: { password: deletePassword }, headers: { Authorization: `Bearer ${token}` } }
      );
      localStorage.removeItem('token');
      navigate('/');
    } catch (err: any) {
      setDeleteMsg({ type: 'error', text: err.response?.data?.message || 'Failed to delete account' });
      setDeleting(false);
    }
  };

  const userInitial = user?.email?.[0]?.toUpperCase() || '?';
  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : '—';

  const navItems = [
    { key: 'profile', label: 'Overview', icon: LayoutDashboard, onClick: () => navigate('/dashboard') },
    { key: 'accounts', label: 'AWS Accounts', icon: Layers, onClick: () => navigate('/connect-account') },
    { key: 'trends', label: 'Cost Trends', icon: Activity, disabled: true },
    { key: 'recommendations', label: 'Recommendations', icon: TrendingDown, disabled: true },
  ];

  const settingsSections = [
    { key: 'profile', label: 'Profile', icon: User },
    { key: 'security', label: 'Security', icon: Lock },
    { key: 'appearance', label: 'Appearance', icon: Palette },
    { key: 'danger', label: 'Danger Zone', icon: Trash2 },
  ] as const;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="flex justify-center animate-pulse">
          <CloudMark size={56} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex">

      {/* Sidebar */}
      <aside className="w-64 flex-shrink-0 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <CloudMark size={56} />
            <Wordmark size={15} />
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                onClick={item.onClick}
                disabled={item.disabled}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 font-medium text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Icon className="w-4 h-4" />
                {item.label}
                {item.disabled && <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">Soon</span>}
              </button>
            );
          })}
        </nav>

        <div className="px-3 py-4 border-t border-gray-200 dark:border-gray-800 space-y-1">
          <button
            onClick={() => navigate('/settings')}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[#e8f0ec] dark:bg-[#0F3D2E]/20 text-emerald-800 dark:text-emerald-400 font-medium text-sm"
          >
            <SettingsIcon className="w-4 h-4" />
            Settings
          </button>
          <button
            onClick={toggle}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 font-medium text-sm transition-colors"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            {theme === 'dark' ? 'Light mode' : 'Dark mode'}
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 font-medium text-sm transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
          <div className="flex items-center gap-3 px-3 pt-3 mt-1 border-t border-gray-200 dark:border-gray-800">
            <div className="w-8 h-8 rounded-full bg-[#0F3D2E] flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
              {userInitial}
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-900 dark:text-white font-medium truncate">{user?.email}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500">Free plan</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Top bar */}
        <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-8 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h1 className="text-gray-900 dark:text-white font-semibold text-lg">Settings</h1>
            <p className="text-gray-400 dark:text-gray-500 text-xs mt-0.5">Manage your account preferences</p>
          </div>
        </header>

        <div className="flex-1 flex overflow-hidden">

          {/* Settings sub-nav */}
          <div className="w-52 flex-shrink-0 border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-4 space-y-1">
            {settingsSections.map(s => {
              const Icon = s.icon;
              const isDanger = s.key === 'danger';
              return (
                <button
                  key={s.key}
                  onClick={() => setActiveSection(s.key)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeSection === s.key
                      ? isDanger
                        ? 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400'
                        : 'bg-[#e8f0ec] dark:bg-[#0F3D2E]/20 text-emerald-800 dark:text-emerald-400'
                      : isDanger
                        ? 'text-red-500 dark:text-red-400/70 hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {s.label}
                </button>
              );
            })}
          </div>

          {/* Settings content */}
          <main className="flex-1 overflow-auto px-8 py-8">

            {/* Profile */}
            {activeSection === 'profile' && (
              <div className="max-w-lg space-y-6">
                <div>
                  <h2 className="text-gray-900 dark:text-white font-semibold text-base mb-1">Profile</h2>
                  <p className="text-gray-400 dark:text-gray-500 text-sm">Your account information</p>
                </div>

                <div className="flex items-center gap-4 p-5 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800">
                  <div className="w-14 h-14 rounded-full bg-[#0F3D2E] flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                    {userInitial}
                  </div>
                  <div>
                    <p className="text-gray-900 dark:text-white font-semibold">{user?.email}</p>
                    <p className="text-gray-400 dark:text-gray-500 text-sm">Free plan · Member since {memberSince}</p>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
                  <div className="px-5 py-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-0.5">Email address</p>
                      <p className="text-sm text-gray-900 dark:text-white font-medium">{user?.email}</p>
                    </div>
                    <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-md">Read-only</span>
                  </div>
                  <div className="px-5 py-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-0.5">Account ID</p>
                      <p className="text-sm text-gray-900 dark:text-white font-mono">#{user?.id}</p>
                    </div>
                  </div>
                  <div className="px-5 py-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-0.5">Member since</p>
                      <p className="text-sm text-gray-900 dark:text-white font-medium">{memberSince}</p>
                    </div>
                  </div>
                  <div className="px-5 py-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-0.5">Plan</p>
                      <p className="text-sm text-gray-900 dark:text-white font-medium">Free — Open Beta</p>
                    </div>
                    <span className="text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-500/10 px-2 py-1 rounded-full font-semibold">Active</span>
                  </div>
                </div>
              </div>
            )}

            {/* Security */}
            {activeSection === 'security' && (
              <div className="max-w-lg space-y-6">
                <div>
                  <h2 className="text-gray-900 dark:text-white font-semibold text-base mb-1">Security</h2>
                  <p className="text-gray-400 dark:text-gray-500 text-sm">Manage your password and account security</p>
                </div>

                <form onSubmit={handleChangePassword} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 space-y-4">
                  <h3 className="text-gray-900 dark:text-white font-semibold text-sm">Change Password</h3>

                  {passwordMsg && (
                    <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                      passwordMsg.type === 'success'
                        ? 'bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-500/30'
                        : 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-500/30'
                    }`}>
                      {passwordMsg.type === 'success'
                        ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                        : <AlertTriangle className="w-4 h-4 flex-shrink-0" />}
                      {passwordMsg.text}
                    </div>
                  )}

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-medium">Current password</label>
                      <div className="relative">
                        <input
                          type={showCurrent ? 'text' : 'password'}
                          value={currentPassword}
                          onChange={e => setCurrentPassword(e.target.value)}
                          required
                          className="w-full px-3 py-2.5 pr-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-800 focus:border-transparent transition"
                          placeholder="Enter current password"
                        />
                        <button type="button" onClick={() => setShowCurrent(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                          {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-medium">New password</label>
                      <div className="relative">
                        <input
                          type={showNew ? 'text' : 'password'}
                          value={newPassword}
                          onChange={e => setNewPassword(e.target.value)}
                          required
                          className="w-full px-3 py-2.5 pr-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-800 focus:border-transparent transition"
                          placeholder="At least 8 characters"
                        />
                        <button type="button" onClick={() => setShowNew(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                          {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-medium">Confirm new password</label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        required
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-800 focus:border-transparent transition"
                        placeholder="Repeat new password"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={savingPassword}
                    className="px-4 py-2 bg-[#0F3D2E] hover:bg-[#0d5236] disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    {savingPassword ? 'Saving…' : 'Update password'}
                  </button>
                </form>
              </div>
            )}

            {/* Appearance */}
            {activeSection === 'appearance' && (
              <div className="max-w-lg space-y-6">
                <div>
                  <h2 className="text-gray-900 dark:text-white font-semibold text-base mb-1">Appearance</h2>
                  <p className="text-gray-400 dark:text-gray-500 text-sm">Choose how CloudFrugal looks for you</p>
                </div>

                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 space-y-4">
                  <h3 className="text-gray-900 dark:text-white font-semibold text-sm">Theme</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {/* Light */}
                    <button
                      onClick={() => theme === 'dark' && toggle()}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        theme === 'light'
                          ? 'border-blue-500 bg-[#e8f0ec] dark:bg-[#e8f0ec]0/10'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center mb-3 shadow-sm">
                        <Sun className="w-4 h-4 text-amber-500" />
                      </div>
                      <p className="text-gray-900 dark:text-white text-sm font-semibold">Light</p>
                      <p className="text-gray-400 dark:text-gray-500 text-xs mt-0.5">Clean white interface</p>
                      {theme === 'light' && (
                        <div className="mt-2 flex items-center gap-1 text-emerald-800 dark:text-emerald-400 text-xs font-medium">
                          <CheckCircle2 className="w-3 h-3" /> Active
                        </div>
                      )}
                    </button>

                    {/* Dark */}
                    <button
                      onClick={() => theme === 'light' && toggle()}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        theme === 'dark'
                          ? 'border-blue-500 bg-[#e8f0ec] dark:bg-[#e8f0ec]0/10'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-gray-900 border border-gray-700 flex items-center justify-center mb-3">
                        <Moon className="w-4 h-4 text-emerald-400" />
                      </div>
                      <p className="text-gray-900 dark:text-white text-sm font-semibold">Dark</p>
                      <p className="text-gray-400 dark:text-gray-500 text-xs mt-0.5">Easy on the eyes</p>
                      {theme === 'dark' && (
                        <div className="mt-2 flex items-center gap-1 text-emerald-800 dark:text-emerald-400 text-xs font-medium">
                          <CheckCircle2 className="w-3 h-3" /> Active
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Danger Zone */}
            {activeSection === 'danger' && (
              <div className="max-w-lg space-y-6">
                <div>
                  <h2 className="text-red-600 dark:text-red-400 font-semibold text-base mb-1">Danger Zone</h2>
                  <p className="text-gray-400 dark:text-gray-500 text-sm">Irreversible actions — proceed with care</p>
                </div>

                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-red-200 dark:border-red-500/30 p-6 space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-red-50 dark:bg-red-500/10 flex items-center justify-center flex-shrink-0">
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </div>
                    <div>
                      <p className="text-gray-900 dark:text-white font-semibold text-sm">Delete account</p>
                      <p className="text-gray-400 dark:text-gray-500 text-xs mt-0.5">
                        Permanently removes your account, all connected AWS accounts, and all data. This cannot be undone.
                      </p>
                    </div>
                  </div>

                  <form onSubmit={handleDeleteAccount} className="space-y-3 pt-2 border-t border-gray-100 dark:border-gray-800">
                    {deleteMsg && (
                      <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                        deleteMsg.type === 'error'
                          ? 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-500/30'
                          : 'bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-400'
                      }`}>
                        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                        {deleteMsg.text}
                      </div>
                    )}
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-medium">Confirm with your password</label>
                      <input
                        type="password"
                        value={deletePassword}
                        onChange={e => setDeletePassword(e.target.value)}
                        required
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition"
                        placeholder="Your password"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-medium">
                        Type <span className="font-mono text-red-500">delete my account</span> to confirm
                      </label>
                      <input
                        type="text"
                        value={deleteConfirm}
                        onChange={e => setDeleteConfirm(e.target.value)}
                        required
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition"
                        placeholder="delete my account"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={deleting || deleteConfirm !== 'delete my account' || !deletePassword}
                      className="px-4 py-2 bg-red-600 hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      {deleting ? 'Deleting…' : 'Delete my account'}
                    </button>
                  </form>
                </div>
              </div>
            )}

          </main>
        </div>
      </div>
    </div>
  );
}
